import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import type { CampaignDoc, CampaignHostDoc } from '@/types/models';
import type { MissingCampaignHost } from './campaigns';

export interface CreateAuditResp {
  auditId: string;
  uploadPath: string;
  /** Code d'appairage AUDIT-XXXX-XXXX, affiché une seule fois (jamais stocké en clair). */
  pairingCode: string;
  /** Expiration du code : epoch ms ou ISO 8601 selon le backend (voir `pairingExpiryDate`). */
  pairingExpiresAt: number | string;
}
export interface CreateAuditReq {
  orgId: string;
  displayName: string;
  /** Case de consentement cochée (obligatoire : sinon FAILED_PRECONDITION 'consent_required'). */
  consent: true;
}
export const callCreateAudit = httpsCallable<CreateAuditReq, CreateAuditResp>(functions, 'createAudit');

/** admin_global : tout audit ; org_admin : audits de son org en statut failed/uploading (cf. canDeleteAudit). */
export interface DeleteAuditReq { auditId: string }
export const callDeleteAudit = httpsCallable<DeleteAuditReq, { ok: true; auditId: string; deletedObjects: number }>(functions, 'deleteAudit');

export interface InviteUserReq { orgId: string; email: string; role: 'org_admin' | 'org_member' }
export const callInviteUser = httpsCallable<InviteUserReq, { inviteId: string }>(functions, 'inviteUser');

export interface CreateOrgReq { displayName: string; legalName?: string; primaryContactEmail: string; primaryContactName: string; }
export const callCreateOrg = httpsCallable<CreateOrgReq, { orgId: string }>(functions, 'createOrg');

export interface AcceptInviteReq { inviteId: string }
export const callAcceptInvite = httpsCallable<AcceptInviteReq, { orgId: string; role: string }>(functions, 'acceptInvite');

export interface GenerateExportReq {
  auditId: string;
  type: 'pdf_executive' | 'pdf_full' | 'excel' | 'zip_bundle';
  /** Optional override; backend falls back to user profile locale, then FR. */
  locale?: 'fr' | 'en';
}
export const callGenerateExport = httpsCallable<GenerateExportReq, { exportId: string; signedUrl: string }>(functions, 'generateExport');

export interface SetExportPublishedReq { auditId: string; exportId: string; published: boolean }
export const callSetExportPublished = httpsCallable<SetExportPublishedReq, { ok: true; exportId: string; publishedToClient: boolean }>(functions, 'setExportPublished');

export interface SignedDownloadReq { kind: 'export' | 'release' | 'audit_zip' | 'document'; path: string }
export const callSignedDownload = httpsCallable<SignedDownloadReq, { url: string; expiresAt: number }>(functions, 'signedDownload');

export interface QuestionnairePrefillReq { orgId?: string; lang?: 'fr' | 'en' }
export interface QuestionnairePrefillField { name: string; label: string; value: string }
/** Valeur pré-remplie : chaîne, ou liste de valeurs d'options pour un champ à
 *  cases multiples (l'embed hydrate les tableaux via `values.includes`). */
export type QuestionnairePrefillValue = string | string[];
export interface QuestionnairePrefillResp {
  auditId: string | null;
  systemName: string | null;
  /** {nom_de_champ: valeur} — faits mesurés ; à fusionner SOUS les réponses saisies. */
  prefill: Record<string, QuestionnairePrefillValue>;
  fields: QuestionnairePrefillField[];
}
export const callQuestionnairePrefill =
  httpsCallable<QuestionnairePrefillReq, QuestionnairePrefillResp>(functions, 'questionnairePrefill');

export interface ReadAuditAssetReq {
  auditId: string;
  path: string;
  /** NDJSON seulement : première LIGNE de la page (défaut 0). */
  offset?: number;
  /** NDJSON seulement : nombre de lignes maximum (défaut 5 000, plafond 50 000). */
  limit?: number;
}
export interface ReadAuditAssetResp {
  path: string;
  text: string;
  /** Renseignés dès qu'`offset`/`limit` est passé : de quoi boucler jusqu'à `eof`. */
  offset?: number;
  lineCount?: number | null;
  /** Faux tant qu'il reste des lignes à lire après cette page. */
  eof?: boolean;
  /** Vrai si la page a été coupée par le budget d'octets avant d'atteindre `limit`. */
  truncated?: boolean;
}
/**
 * Lecture d'un actif d'audit (journalisée côté fonctions dans auditLog) :
 * raw_logs/* → admin_global seulement ; json/* → org_admin ou admin_global.
 *
 * `offset`/`limit` paginent un NDJSON en LIGNES : c'est ce qui permet de
 * charger un inventaire de plusieurs centaines de milliers de fichiers niveau
 * par niveau au lieu de tout tirer d'un coup. Les contrôles d'accès sont
 * exactement les mêmes qu'une lecture entière.
 */
export const callReadAuditAsset = httpsCallable<ReadAuditAssetReq, ReadAuditAssetResp>(functions, 'readAuditAsset');

export interface PublishReleaseReq {
  version: string;
  channel: 'stable' | 'beta';
  os: 'windows' | 'macos' | 'linux';
  arch: 'x86_64' | 'arm64' | 'universal';
  variant: 'onefile' | 'onedir';
  releaseNotes: string;
  sha256: string;
  sizeBytes: number;
  fileName: string;
}
export const callPublishRelease = httpsCallable<PublishReleaseReq, { releaseId: string; uploadPath: string }>(functions, 'publishRelease');

export interface AssumeOrgReq { orgId: string | null }
export const callAdminAssumeOrg = httpsCallable<AssumeOrgReq, { ok: true }>(functions, 'adminAssumeOrg');

export interface CreateUserDirectReq {
  orgId: string;
  email: string;
  displayName: string;
  role: 'org_admin' | 'org_member';
  tempPassword?: string;
  sendResetEmail?: boolean;
}
export interface CreateUserDirectResp {
  uid: string;
  email: string;
  tempPassword: string;
  passwordResetLink: string | null;
}
export const callCreateUserDirect = httpsCallable<CreateUserDirectReq, CreateUserDirectResp>(functions, 'createUserDirect');

export interface SetMemberRoleReq { orgId: string; uid: string; role: 'org_admin' | 'org_member' }
export const callSetMemberRole = httpsCallable<SetMemberRoleReq, { ok: true }>(functions, 'setMemberRole');

export interface RemoveMemberReq { orgId: string; uid: string }
export const callRemoveMember = httpsCallable<RemoveMemberReq, { ok: true; removedUid: string }>(functions, 'removeMember');

export interface RevokeInvitationReq { inviteId: string }
export const callRevokeInvitation = httpsCallable<RevokeInvitationReq, { ok: true }>(functions, 'revokeInvitation');

export interface GlobalAdmin {
  uid: string;
  email: string;
  displayName?: string | null;
  createdAt?: number | null;
  lastSignIn?: number | null;
}
export const callListGlobalAdmins = httpsCallable<void, { admins: GlobalAdmin[] }>(functions, 'listGlobalAdmins');

export interface PromoteGlobalReq { email: string }
export const callPromoteGlobalAdmin = httpsCallable<PromoteGlobalReq, { uid: string; email: string }>(functions, 'promoteGlobalAdmin');

export interface DemoteGlobalReq { uid: string }
export const callDemoteGlobalAdmin = httpsCallable<DemoteGlobalReq, { ok: true }>(functions, 'demoteGlobalAdmin');

export interface CreateGlobalAdminReq { email: string; displayName: string; tempPassword?: string }
export interface CreateGlobalAdminResp {
  uid: string;
  email: string;
  tempPassword: string | null;
  passwordResetLink: string | null;
  alreadyExisted: boolean;
}
export const callCreateGlobalAdminDirect = httpsCallable<CreateGlobalAdminReq, CreateGlobalAdminResp>(functions, 'createGlobalAdminDirect');

// ── Toolkit entitlements ──────────────────────────────────────────────────────
export interface SetOrgToolkitsReq { orgId: string; toolkits: string[] }
export const callSetOrgToolkits = httpsCallable<SetOrgToolkitsReq, { ok: true }>(functions, 'setOrgToolkits');

// ── Generic org documents ─────────────────────────────────────────────────────
export interface CreateOrgDocumentReq { orgId: string; kind: string; title: string; description?: string }
export interface CreateOrgDocumentResp { docId: string; uploadPath: string }
export const callCreateOrgDocument = httpsCallable<CreateOrgDocumentReq, CreateOrgDocumentResp>(functions, 'createOrgDocument');

export interface SetDocumentPublishedReq { docId: string; published: boolean }
export const callSetDocumentPublished = httpsCallable<SetDocumentPublishedReq, { ok: true; docId: string; publishedToClient: boolean }>(functions, 'setDocumentPublished');

export interface DeleteOrgDocumentReq { docId: string }
export const callDeleteOrgDocument = httpsCallable<DeleteOrgDocumentReq, { ok: true }>(functions, 'deleteOrgDocument');

// ── Support Tickets ───────────────────────────────────────────────────────────

export interface AttachmentMeta {
  contentType: string;
  sizeBytes: number;
  fileName: string;
}

interface TicketUploadSlot { id: string; path: string }

export interface CreateTicketReq {
  orgId: string;
  subject: string;
  category: 'platform' | 'ai_system' | 'account' | 'other';
  description: string;
  attachmentMeta?: AttachmentMeta[];
}
export interface CreateTicketResp {
  ticketId: string;
  messageId: string;
  uploads: TicketUploadSlot[];
}
export const callCreateTicket = httpsCallable<CreateTicketReq, CreateTicketResp>(functions, 'createTicket');

export interface AddTicketMessageReq {
  ticketId: string;
  body: string;
  attachmentMeta?: AttachmentMeta[];
}
export interface AddTicketMessageResp {
  messageId: string;
  uploads: TicketUploadSlot[];
}
export const callAddTicketMessage = httpsCallable<AddTicketMessageReq, AddTicketMessageResp>(functions, 'addTicketMessage');

export interface UpdateTicketStatusReq {
  ticketId: string;
  status: 'open' | 'in_progress' | 'closed';
}
export const callUpdateTicketStatus = httpsCallable<UpdateTicketStatusReq, { ok: true }>(functions, 'updateTicketStatus');
// ── Campagnes (un code, N machines, un consolidé, une facture) ────────────────

export interface CreateCampaignReq {
  orgId: string;
  displayName: string;
  /** Machines annoncées (facultatif) : elles apparaissent « attendues » dès la création. */
  hostsExpected?: string[];
  /** Durée de validité du code en jours (défaut 30, plafond 90 côté fonctions). */
  ttlDays?: number;
  /** Case de consentement cochée (obligatoire : sinon FAILED_PRECONDITION 'consent_required'). */
  consent: true;
}
export interface CreateCampaignResp {
  campaignId: string;
  /**
   * Code AUDIT-XXXX-XXXX valable pour TOUTES les machines de la campagne.
   * Affiché UNE SEULE FOIS : seul `sha256(code + pepper)` est stocké, aucun
   * appel ne peut le relire ensuite (getCampaign ne le renvoie jamais).
   */
  campaignCode: string;
  /** Échéance du code, ISO 8601. */
  expiresAt: string;
  /** Slugs normalisés effectivement retenus par le serveur. */
  hostsExpected: string[];
}
export const callCreateCampaign = httpsCallable<CreateCampaignReq, CreateCampaignResp>(functions, 'createCampaign');

/** Clôture : le code n'accepte plus de machine, le consolidé reste lisible. org_admin (sa propre org) ou admin_global. */
export interface CloseCampaignReq { campaignId: string }
export const callCloseCampaign = httpsCallable<CloseCampaignReq, { ok: true; campaignId: string; codesExpired: number }>(functions, 'closeCampaign');

export interface GetCampaignReq { campaignId: string }
export interface GetCampaignResp {
  /** Le document de campagne, SANS le code (il n'existe en clair nulle part). */
  campaign: CampaignDoc & { campaignId: string };
  hosts: CampaignHostDoc[];
  missingHosts: MissingCampaignHost[];
}
export const callGetCampaign = httpsCallable<GetCampaignReq, GetCampaignResp>(functions, 'getCampaign');
